import { Fragment } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { plainButtonSx, primaryButtonSx } from './styles';
import { isCommandRisky } from '@/lib/shell-command';
import { SHELL_MONO, TONES, toneText } from '@/theme/tokens';

export interface RunSummary {
  devices: number;
  online: number;
  offline: number;
  /** Super admins only. */
  tenant?: string;
  command: string;
  /** What the run is for; undefined when left empty. */
  description?: string;
  timeout: string;
  concurrency: number;
  /** The formatted deadline; undefined means none. */
  deadline?: string;
  /** Set only when the rollout is not "All at once". */
  rollout?: string;
  /** Send and forget: a batch of `concurrency` devices every `slotRelease`. */
  noTimeout: boolean;
  slotRelease: string;
  /** A Filter, All or Group run with a deadline: later matches join until then. */
  joinUntil?: string;
  /** Targeted devices already in other active runs. */
  overlap?: { devices: number; runs: number };
}

interface ConfirmRunDialogProps {
  open: boolean;
  summary: RunSummary;
  pending: boolean;
  error?: string;
  onCancel: () => void;
  onRun: () => void;
}

const plural = (n: number, word: string) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

/** The last check before a command goes out: it repeats what is about to happen. */
export function ConfirmRunDialog({ open, summary, pending, error, onCancel, onRun }: ConfirmRunDialogProps) {
  const runLabel = `Run on ${plural(summary.devices, 'device')}`;
  const rows: [string, string][] = [];
  if (summary.tenant) rows.push(['Tenant', summary.tenant]);
  rows.push(['Command', `$ ${summary.command}`]);
  if (summary.description) rows.push(['Description', summary.description]);
  rows.push(
    ['Devices', `${summary.online.toLocaleString()} online now · ${summary.offline.toLocaleString()} offline${summary.joinUntil ? ` · later matches join until ${summary.joinUntil}` : ''}`],
    ['Timeout', summary.noTimeout ? `None (send and forget) · ${summary.concurrency} devices every ${summary.slotRelease}` : `${summary.timeout} per device · ${summary.concurrency} at a time`],
    ['Deadline', summary.deadline ?? 'None — offline devices run when back, until cancelled'],
  );
  if (summary.rollout) rows.push(['Rollout', summary.rollout]);

  return (
    <Dialog
      open={open}
      onClose={pending ? undefined : onCancel}
      maxWidth={false}
      aria-labelledby="confirm-run-title"
      PaperProps={{
        sx: {
          width: 'min(460px, calc(100vw - 32px))',
          m: 2,
          p: 2.5,
          borderRadius: '8px',
          border: 1,
          borderColor: 'divider',
          boxShadow: '0 12px 32px rgba(0,0,0,.25)',
        },
      }}
    >
      <Typography component="h2" id="confirm-run-title" sx={{ mb: 1.5, fontSize: 16, fontWeight: 700 }}>
        {runLabel}?
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '96px minmax(0, 1fr)', columnGap: 1.5, rowGap: 0.5, pb: 2, fontSize: 13 }}>
        {rows.map(([label, value]) => (
          <Fragment key={label}>
            <Typography component="span" sx={{ fontSize: 13, fontWeight: 500, color: 'text.secondary' }}>
              {label}
            </Typography>
            <Typography
              component="span"
              sx={{ fontSize: label === 'Command' ? 12 : 13, overflowWrap: 'anywhere', ...(label === 'Command' ? { fontFamily: SHELL_MONO, whiteSpace: 'pre-wrap' } : {}) }}
            >
              {value}
            </Typography>
          </Fragment>
        ))}
      </Box>
      {summary.overlap && summary.overlap.devices > 0 && (
        <Box
          sx={{
            mb: 2,
            px: 1.25,
            py: 1,
            borderRadius: '6px',
            fontSize: 12,
            color: toneText('warning'),
            bgcolor: (t) => alpha(TONES.warning.main, t.palette.mode === 'dark' ? 0.18 : 0.12),
          }}
        >
          ⚠ {summary.overlap.devices.toLocaleString()} of these devices are also in {summary.overlap.runs.toLocaleString()} active{' '}
          {summary.overlap.runs === 1 ? 'run' : 'runs'}. Commands on the same device run one after another.
        </Box>
      )}
      {isCommandRisky(summary.command) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>{summary.command.trim().split(/\s+/)[0]}</strong> can change or break devices.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button autoFocus variant="outlined" onClick={onCancel} disabled={pending} sx={plainButtonSx}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onRun}
          disabled={pending}
          startIcon={pending ? <CircularProgress size={14} color="inherit" /> : undefined}
          sx={primaryButtonSx}
        >
          {runLabel}
        </Button>
      </Box>
    </Dialog>
  );
}
